import { type RouteRecordRaw } from 'vue-router';
import { i18n } from '@/plugins/i18n';
import { useDashboardStore, useSessionStore, useLocalizationStore } from '@/store';

import * as Routes from '@/router/spaRoutes';
import { AuthPolicyEnum } from '@/constants/authPolicyEnum';

//Components
import TheMobileAppPromo from '@/components/TheMobileAppPromo.vue';
import LibraryVideoItem from '@/pages/Education/components/LibraryEducationVideo.vue';
import LibraryHtmlItem from '@/pages/Education/components/LibraryEducationHtml.vue';

// Pages
import TheWelcomePage from '@/pages/Login/TheWelcomePage.vue';
import SystemMaintenance from '@/pages/SystemMaintenance/TheSysMaint.vue';

import Authenticate from '@/pages/Authenticate.vue';
import ConfirmIdentity from '@/pages/ConfirmIdentity.vue';
import TheSmsOptOut from '@/pages/TheSmsOptOut.vue';

import TheActivationComplete from '@/pages/Activation/TheActivationComplete.vue';
import TheActivateCreatePassword from '@/pages/Activation/TheActivateCreatePassword.vue';
import TheActivateCreateUsername from '@/pages/Activation/TheActivateCreateUsername.vue';
import TheActivateEULA from '@/pages/Activation/TheActivateEULA.vue';
import TheActivateCrossBorderDataTransfer from '@/pages/Activation/TheActivateCrossBorderDataTransfer.vue';
import TheActivateGlobalConsent from '@/pages/Activation/TheActivateGlobalConsent.vue';
import TheActivateOverview from '@/pages/Activation/TheActivateOverview.vue';
import TheActivatePrivacyPolicy from '@/pages/Activation/TheActivatePrivacyPolicy.vue';
import TheActivateProfileInformation from '@/pages/Activation/TheActivateProfileInformation.vue';
import TheActivateProfilePhoto from '@/pages/Activation/TheActivateProfilePhoto.vue';
import TheActivateStart from '@/pages/Activation/TheActivateStart.vue';
import TheLoginExistingForm from '@/pages/Activation/TheLoginExistingForm.vue';

import TheLoginForm from '@/pages/Login/TheLoginForm.vue';
import TheForgotUsername from '@/pages/Login/TheForgotUsername.vue';
import TheForgotPassword from '@/pages/Login/TheForgotPassword.vue';
import TheSetPassword from '@/pages/Login/TheSetPassword.vue';
import TheAppDownload from '@/pages/AppDownload/TheAppDownload.vue';

import TheMagicLinkForm from '@/pages/Login/TheMagicLinkForm.vue';
import TheMagicLinkIntro from '@/pages/Survey/TheMagicLinkIntro.vue';
import TheMagicLinkDashboardHome from '@/pages/DashboardHome/TheMagicLinkDashboardHome.vue';

import TheSelectCarePlan from '@/pages/TheSelectCarePlan.vue';
import TheInterrupts from '@/pages/TheInterrupts.vue';
import TheAboutYourData from '@/pages/TheAboutYourData.vue';
import AboutYourCommunicationPreferences from '@/pages/AboutYourCommunicationPreferences.vue';
import CommunicationPreferences from '@/pages/CommunicationPreferences.vue';
import TheDashboard from '@/pages/TheDashboard.vue';

import TheDashboardHome from '@/pages/DashboardHome/TheDashboardHome.vue';

import TheDashboardMyStats from '@/pages/DashboardMyStats/TheDashboardMyStats.vue';
import MyRecoveryProgress from '@/pages/DashboardMyStats/MyRecoveryProgress.vue';

import TheDashboardEducation from '@/pages/Education/TheDashboardEducation.vue';
import ThePreSurgeryEducationLibrary from '@/pages/Education/ThePreSurgeryEducationLibrary.vue';
import ThePostSurgeryEducationLibrary from '@/pages/Education/ThePostSurgeryEducationLibrary.vue';
import TheNonSurgicalEducationLibrary from '@/pages/Education/ThePostSurgeryEducationLibrary.vue';
import TheTechnologyEducationLibrary from '@/pages/Education/TheTechnologyEducationLibrary.vue';
import TheROSAKneeEducationLibrary from '@/pages/Education/TheROSAKneeEducationLibrary.vue';

import TheDashboardMessages from '@/pages/DashboardMessages/TheDashboardMessages.vue';
import TheYourCareTeam from '@/pages/DashboardMessages/components/TheYourCareTeam.vue';

import Education from '@/pages/Education/Education.vue';

import TheSurveyContainer from '@/pages/Survey/TheSurveyContainer.vue';
import TheWizardContainer from '@/pages/Survey/TheWizardContainer.vue';

import Routine from '@/pages/Routine/Routine.vue';
import Exercise from '@/pages/Routine/Exercise.vue';
import RateRoutine from '@/pages/Routine/RateRoutine.vue';
import CompletedRoutine from '@/pages/Routine/CompletedRoutine.vue';

import ThePainRating from '@/pages/DashboardHome/ThePainRating.vue';
import TheDailyWalkingGoal from '@/pages/DashboardHome/TheDailyWalkingGoal.vue';

import RcvyUpdtsList from "@/pages/RecoveryUpdates/RcvyUpdtsList.vue";
import RcvyUpdtsDetail from "@/pages/RecoveryUpdates/RcvyUpdtsDetail.vue";

//Settings
import TheSettingsMenu from '@/pages/TheSettings/TheSettingsMenu.vue';
import ThePatientDetails from '@/pages/TheSettings/ThePatientDetails.vue';
import TheSupport from '@/pages/TheSettings/TheSupport.vue';
import TheAbout from '@/pages/TheSettings/TheAbout.vue';
import TheLegalAndPrivacy from '@/pages/TheSettings/TheLegalAndPrivacy.vue';
import TheSettingsPrivacyPolicy from '@/pages/TheSettings/TheSettingsPrivacyPolicy.vue';
import TheSettingsSafetyInformation from '@/pages/TheSettings/TheSettingsSafetyInformation.vue';
import TheSettingsEULA from '@/pages/TheSettings/TheSettingsEULA.vue';
import TheChangePassword from '@/pages/TheSettings/TheChangePassword.vue';

//PIQ Tasks
// Setup Segment 1
import TheStartPage from '@/pages/PIQSetup/v2/TheStartPage.vue'
import TheCanaryTermsandConditions from '@/pages/PIQSetup/v2/TheCanaryTermsandConditions.vue';
import TheCanaryPrivacyPolicy from '@/pages/PIQSetup/v2/TheCanaryPrivacyPolicy.vue';
import TheAddressForm from '@/pages/PIQSetup/v2/TheAddressForm.vue';
import TheCaregiverForm from '@/pages/PIQSetup/v2/TheCaregiverForm.vue';
import TheHeightWeightForm from '@/pages/PIQSetup/v2/TheHeightWeightForm.vue';

// Setup Segment 2
import TheBaseStationSetup from '@/pages/PIQSetup/TheBaseStationSetup.vue';
import TheBaseStationIntro from '@/pages/PIQSetup/v2/TheBaseStationIntro.vue';
import TheBaseStationVersionSelect from '@/pages/PIQSetup/v2/TheBaseStationVersionSelect.vue';
import TheTimezoneSelection from '@/pages/PIQSetup/v2/TheTimezoneSelection.vue';
import TheComputerRequirements from '@/pages/PIQSetup/v2/TheComputerRequirements.vue';

//setup Segment 3
import TheCanaryAlmostCompletePage from '@/pages/PIQSetup/v2/TheCanaryAlmostCompletePage.vue';
//finalize
import TheCompletePage from '@/pages/PIQSetup/v1/TheCompletePage.vue'

import { PIQSetupPages } from '@/services/piqSetupService';
import { StatTypeEnum } from '@/constants/measurementTypes';

import { EngagementSurveyTypeEnum } from '@/constants/survey';

// iFrame pages
import medStratPage from '@/pages/Images/MedStratPage.vue';

// WaitList pages
import SetSurgeryDate from '@/pages/Procedure/SetSurgeryDate.vue';
import TheWaitlistInterrupt from '@/pages/TheWaitlistInterrupt.vue';
import { InterruptType } from '@/constants/enums';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: { name: 'Login' }
  },
  {
    path: '/SystemMaintenance',
    name: 'SystemMaintenance',
    component: SystemMaintenance,
    meta: { allowAnonymous: true },
  },
  {
    path: '/Authenticate/:returnPath?',
    name: 'Authenticate',
    component: Authenticate,
    meta: { allowAnonymous: true },
    props: route => ({ ...route.query, ...route.params })
  },
  {
    path: '/Welcome',
    name: 'Welcome',
    component: TheWelcomePage,
    meta: {
      allowAnonymous: true
    }
  },
  {
    path: Routes.loginForm,
    name: 'Login',
    component: TheLoginForm,
    meta: {
      allowAnonymous: true,
      title: []
    }
  },
  {
    path: Routes.appDownload,
    name: 'AppDownload',
    component: TheAppDownload,
    meta: {
      allowAnonymous: true,
      title: ['download_app_webpage_header']
    }
  },
  {
    path: '/ml/:magicLinkToken?',
    name: 'MagicLink',
    component: TheMagicLinkForm,
    props: true,
    meta: {
      allowAnonymous: true,
      title: []
    }
  },
  {
    path: '/SmsOptOut/:smsOptOutCode?',
    name: 'SmsOptOut',
    component: TheSmsOptOut,
    props: true,
    meta: {
      allowAnonymous: true,
      title: []
    }
  },
  {
    path: '/MLIntro',
    name: 'MagicLinkIntro',
    component: TheMagicLinkIntro,
    meta: {
      policy: AuthPolicyEnum.FullTrustOrMagicLinkTrustPatient,
      title: []
    }
  },
  {
    path: '/PIQSetup/Start',
    name: `PIQSetup.${PIQSetupPages.Start}`,
    component: TheStartPage,
    meta: {
      allowAnonymous: true,
      title: ['piq_account_setup_tasks', 'activate_start']
    }
  },
  {
    path: '/PIQSetup/CanaryTerms',
    name: `PIQSetup.${PIQSetupPages.Terms}`,
    component: TheCanaryTermsandConditions,
    meta: {
      policy: AuthPolicyEnum.FullTrustPatient,
      title: ['piq_account_setup_tasks', 'activate_app_terms']
    }
  },
  {
    path: '/PIQSetup/CanaryPrivacy',
    name: `PIQSetup.${PIQSetupPages.PrivacyPolicy}`,
    component: TheCanaryPrivacyPolicy,
    meta: {
      policy: AuthPolicyEnum.FullTrustPatient,
      title: ['piq_account_setup_tasks', 'privacy_policy_header']
    }
  },
  {
    path: '/PIQSetup/Address',
    name: `PIQSetup.${PIQSetupPages.Address}`,
    component: TheAddressForm,
    meta: {
      allowAnonymous: true,
      title: ['piq_account_setup_tasks', 'piq_mailing_address']
    }
  },
  {
    path: '/PIQSetup/Caregiver',
    name: `PIQSetup.${PIQSetupPages.Caregiver}`,
    component: TheCaregiverForm,
    meta: {
      allowAnonymous: true,
      title: ['piq_account_setup_tasks', 'piq_caregiver_details']
    }
  },
  {
    path: '/PIQSetup/HeightWeight',
    name: `PIQSetup.${PIQSetupPages.HeightWeight}`,
    component: TheHeightWeightForm,
    meta: {
      allowAnonymous: true,
      title: ['piq_account_setup_tasks', 'piq_height', 'piq_weight']
    }
  },
  // New HBS2 pages to be added
  {
    path: '/PIQSetup/CanaryBaseStation',
    name: `PIQSetup.${PIQSetupPages.CanaryHasHBS}`,
    props: true,
    component: TheBaseStationIntro,
    meta: {
      title: ['piq_account_setup_tasks', 'FPO-Has Base Station?']
    }
  },
  {
    path: '/PIQSetup/CanaryBaseStationVersionSelect',
    name: `PIQSetup.${PIQSetupPages.CanaryHBSVersion}`,
    props: true,
    component: TheBaseStationVersionSelect,
    meta: {
      title: ['piq_account_setup_tasks', '?']
    }
  },
  {
    path: '/PIQSetup/BedTime',
    name: `PIQSetup.${PIQSetupPages.CanaryBedtime}`,
    props: true,
    component: TheTimezoneSelection,
    meta: {
      title: ['piq_account_setup_tasks', 'piq_bedtime_selection']
    }
  },
  {
    path: '/PIQSetup/CanaryComputerRequirements',
    name: `PIQSetup.${PIQSetupPages.CanaryHBSComputerRequirements}`,
    props: true,
    component: TheComputerRequirements,
    meta: {
      title: ['piq_account_setup_tasks', '?']
    }
  },
  // HBS Segment 3
  {
    path: '/PIQSetup/CanaryAlmostComplete',
    name: `PIQSetup.${PIQSetupPages.CanaryAlmostDone}`,
    props: true,
    component: TheCanaryAlmostCompletePage,
    meta: {
      title: ['piq_account_setup_tasks', '?']
    }
  },
  {
    path: '/PIQSetup/Complete',
    name: `PIQSetup.${PIQSetupPages.Complete}`,
    component: TheCompletePage,
    meta: {
      title: ['piq_account_setup_tasks', 'piq_profilecomplete']
    }
  },

  {
    path: '/ConfirmIdentity',
    name: 'ConfirmIdentity',
    component: ConfirmIdentity,
    meta: {
      allowAnonymous: true,
      title: ['activate_confirm_identity_title']
    },
    props: (route: any) => {

      const params = route.query;
      params.workflow = parseInt(params.workflow, 10);
      params.day = parseInt(params.day, 10);
      params.month = parseInt(params.month, 10);
      params.year = parseInt(params.year, 10);
      params.progress = parseInt(params.progress, 10);

      return params;
    }
  },
  {
    path: '/Activate',
    name: 'Activate',
    component: TheActivateOverview,
    meta: {
      allowAnonymous: true,
      title: ['activate_account', 'activate_overview']
    }
  },
  {
    path: '/Activate/Start',
    name: 'Activate.Start',
    component: TheActivateStart,
    meta: {
      allowAnonymous: true,
      title: ['activate_account', 'activate_start']
    }
  },
  {
    path: '/Activate/PrivacyPolicy',
    name: 'Activate.PrivacyPolicy',
    component: TheActivatePrivacyPolicy,
    meta: {
      policy: AuthPolicyEnum.UnregisteredPatientWith2FA,
      title: ['activate_account', 'activate_privacy_policy']
    }
  },
  {
    path: '/Activate/EULA',
    name: 'Activate.EULA',
    component: TheActivateEULA,
    meta: {
      // policy: AuthPolicyEnum.UnregisteredPatientWith2FA,
      title: ['activate_account', 'activate_eula']
    }
  },
  {
    path: '/Activate/LoginExisting',
    name: 'Activate.LoginExisting',
    component: TheLoginExistingForm,
    meta: {
      // policy: AuthPolicyEnum.UnregisteredPatientWith2FA,
      title: ['activate_account']
    }
  },
  {
    path: '/Activate/CrossBorderDataTransfer',
    name: 'Activate.CrossBorderDataTransfer',
    component: TheActivateCrossBorderDataTransfer,
    meta: {
      policy: AuthPolicyEnum.UnregisteredPatientWith2FA,
      title: ['activate_account', 'china_activate_cross_border']
    }
  },
  {
    path: '/Activate/GlobalConsent',
    name: 'Activate.GlobalConsent',
    component: TheActivateGlobalConsent,
    meta: {
      policy: AuthPolicyEnum.UnregisteredPatientWith2FA,
      title: ['activate_account', 'consent_global_consent']
    }
  },
  {
    path: '/Activate/CreateUsername',
    name: 'Activate.CreateUsername',
    component: TheActivateCreateUsername,
    meta: {
      policy: AuthPolicyEnum.UnregisteredPatientWith2FA,
      title: ['activate_account', 'activate_username_create_new']
    }
  },
  {
    path: '/Activate/CreatePass',
    name: 'Activate.CreatePassword',
    component: TheActivateCreatePassword,
    props: true,
    meta: {
      policy: AuthPolicyEnum.UnregisteredPatientWith2FA,
      title: ['activate_account', 'password_create_password_capitalized']
    }
  },
  {
    path: '/Activate/ProfileInformation',
    name: 'Activate.ProfileInformation',
    component: TheActivateProfileInformation,
    meta: {
      policy: AuthPolicyEnum.UnregisteredPatientWith2FA,
      title: ['activate_account', 'activate_profile_title']
    }
  },
  {
    path: '/Activate/ProfilePhoto',
    name: 'Activate.ProfilePhoto',
    component: TheActivateProfilePhoto,
    meta: {
      policy: AuthPolicyEnum.UnregisteredPatientWith2FA,
      title: ['activate_account', 'pwe_add_photo']
    }
  },
  {
    path: '/Activate/Complete',
    name: 'Activate.Complete',
    component: TheActivationComplete,
    meta: {
      policy: AuthPolicyEnum.UnregisteredPatientWith2FA,
      title: ['activate_account', 'piq_profilecomplete']
    }
  },
  {
    path: '/ForgotUsername',
    name: 'ForgotUsername',
    component: TheForgotUsername,
    props: true,
    meta: {
      allowAnonymous: true,
      title: ['forgot_username']
    }
  },
  {
    path: '/ForgotPass',
    name: 'ForgotPassword',
    component: TheForgotPassword,
    props: true,
    meta: {
      allowAnonymous: true,
      title: ['forgot_password_title']
    }
  },
  {
    path: '/SetPass',
    name: 'SetPassword',
    component: TheSetPassword,
    props: true,
    meta: {
      policy: AuthPolicyEnum.InForgottonPasswordWorkflow,
      title: ['password_create_password_capitalized']
    },
    beforeEnter: (to, from, next) => {
      const sessionStore = useSessionStore();
      const localizationStore = useLocalizationStore();

      if (sessionStore.hasPasscode)
        localizationStore.routeTitleKeys = ['passcode_create_passcode_capitalized'];

      next();
    }
  },
  {
    path: '/SelectCarePlan',
    name: 'SelectCarePlan',
    component: TheSelectCarePlan,
    props: true,
    meta: {
      policy: AuthPolicyEnum.FullTrustOrMagicLinkTrustPatient,
      title: ['eoc_select_care_plan']
    }
  },
  {
    path: '/Interrupts:reload?',
    name: 'Interrupts',
    component: TheInterrupts,
    props: true,
    meta: {
      policy: AuthPolicyEnum.FullTrustOrMagicLinkTrustPatient,
      title: []
    },
  },
  {
    path: '/AboutYourData',
    name: 'AboutYourData',
    component: TheAboutYourData,
    props: true,
    meta: {
      policy: AuthPolicyEnum.FullTrustPatient,
      title: ['about_your_data_header']
    }
  },
  {
    path: '/Dashboard',
    redirect: { name: 'Dashboard.Home' },
    name: 'Dashboard',
    component: TheDashboard,
    props: true,
    meta: {
      policy: AuthPolicyEnum.FullTrustOrMagicLinkTrustPatient,
    },
    beforeEnter: (to, from, next) => {
      const sessionStore = useSessionStore();
      const dashboardStore = useDashboardStore();
      if (!sessionStore?.user?.selectedEocId) {
        next({ name: 'SelectCarePlan' });
      }
      else if (to.matched.some((item: any) => item.name === 'Settings')) {
        next();
      } else if (dashboardStore.patient?.interruptData?.interrupts?.find(x => x.type == 'Pain_Check_In') != undefined) {
        next({ name: 'PainRating' });
      } else if (dashboardStore.patient?.interruptData?.interrupts?.find(x => x.type === InterruptType.Add_Procedure_Date) != undefined) {
        next({ name: 'Waitlist' })
      }
      else {
        next();
      }
    },
    children: [
      {
        path: 'Home/:reload?',
        name: 'Dashboard.Home',
        components: {
          auxiliary: TheMobileAppPromo,
          default: TheDashboardHome
        },
        meta: {
          title: ['home_home_android']
        },
        props: {
          default: true
        }
      },
      {
        path: 'MLHome/:reload?',
        name: 'Dashboard.MLHome',
        components: {
          default: TheMagicLinkDashboardHome
        },
        meta: {
          title: ['home_home_android']
        },
        props: {
          default: true
        }
      },
      {
        path: 'MyStats/:eocDeviceId?',
        name: 'Dashboard.MyStats',
        components: {
          auxiliary: TheMobileAppPromo,
          default: TheDashboardMyStats
        },
        meta: {
          title: ['stats']
        }
      },
      {
        path: 'MyStats/PersonaIQ/:eocDeviceId?',
        name: 'Dashboard.MyStats.PersonaIQ',
        components: {
          auxiliary: TheMobileAppPromo,
          default: TheDashboardMyStats
        },
        meta: {
          title: ['stats_header_piq', 'stats']
        },
        props: {
          auxiliary: false,
          default: route => ({
            sourceType: StatTypeEnum.PersonaIQ.toString()
          })
        }
      },
      {
        path: 'MyStats/MyProgress/:deviceId?/:metric?',
        name: 'Dashboard.MyStats.MyProgress',
        components: {
          auxiliary: TheMobileAppPromo,
          default: MyRecoveryProgress
        },
        meta: {
          title: ['my_progress_title', 'stats'],
        },
        props: {
          auxiliary: false,
          default: true
        }
      },
      {
        path: 'Education',
        redirect: { name: 'Dashboard.Education.PreSurgery' },
        name: 'Dashboard.Education',
        components: {
          auxiliary: TheMobileAppPromo,
          default: TheDashboardEducation
        },
        meta: {
          title: ['gen_education']
        },
        props: true,
        children: [
          {
            path: 'PreSurgery',
            name: 'Dashboard.Education.PreSurgery',
            components: {
              default: ThePreSurgeryEducationLibrary
            },
            meta: {
              title: ['education_pre_surgery', 'gen_education']
            },
             beforeEnter: (to, from, next) => {
              const sessionStore = useSessionStore();
              if (!sessionStore?.selectedProcedure?.isSurgicalProcedure) {
                next({ name: 'Dashboard.Education.NonSurgical' });
              } else {
                next();
              }
            },
          },
          {
            path: 'PostSurgery',
            name: 'Dashboard.Education.PostSurgery',
            components: {
              default: ThePostSurgeryEducationLibrary
            },
            meta: {
              title: ['education_post_surgery', 'gen_education']
            }
          },
          {
            path: 'NonSurgical',
            name: 'Dashboard.Education.NonSurgical',
            components: {
              default: TheNonSurgicalEducationLibrary
            },
            meta: {
              title: ['education_non_surgical', 'gen_education']
            }
          },
          {
            path: 'Technology',
            name: 'Dashboard.Education.Technology',
            components: {
              default: TheTechnologyEducationLibrary
            },
            meta: {
              title: ['pwe_technology_library', 'gen_education']
            }
          }
        ]
      },
      {
        path: 'Messages',
        name: 'Dashboard.Messages',
        components: {
          auxiliary: TheYourCareTeam,
          default: TheDashboardMessages
        },
        meta: {
          title: ['messages']
        },
        props: true
      }
    ]
  },
  {
    path: '/Settings',
    component: TheDashboard,
    children: [
      {
        path: '/Settings',
        name: 'Settings',
        components: {
          auxiliary: TheMobileAppPromo,
          default: TheSettingsMenu
        },
        meta: {
          title: ['settings']
        },
        props: true
      }
    ]
  },
  {
    path: '/Settings/PatientDetails',
    name: 'Settings.PatientDetails',
    component: ThePatientDetails,
    meta: {
      title: ['gen_patient_details', 'settings']
    }
  },
  {
    path: '/Settings/Support',
    name: 'Settings.Support',
    component: TheSupport,
    meta: {
      title: ['settings_help_support', 'settings']
    }
  },
  {
    path: '/Settings/About',
    name: 'Settings.About',
    component: TheAbout,
    meta: {
      title: ['settings_about', 'settings']
    }
  },
  {
    path: '/Settings/LegalAndPrivacy',
    name: 'Settings.LegalAndPrivacy',
    component: TheLegalAndPrivacy,
    meta: {
      title: ['settings_legal_and_privacy', 'settings']
    }
  },
  {
    path: '/Settings/SafetyInformation',
    name: 'Settings.SafetyInformation',
    component: TheSettingsSafetyInformation,
    meta: {
      title: ['routine_safety', 'settings_legal_and_privacy', 'settings']
    }
  },
  {
    path: '/Settings/PrivacyPolicy',
    name: 'Settings.PrivacyPolicy',
    component: TheSettingsPrivacyPolicy,
    meta: {
      title: ['activate_privacy_policy', 'settings_legal_and_privacy', 'settings']
    }
  },
  {
    path: '/Settings/EULA',
    name: 'Settings.EULA',
    component: TheSettingsEULA,
    meta: {
      title: ['activate_eula', 'settings_legal_and_privacy', 'settings']
    }
  },
  {
    path: '/Settings/ChangePass/:forcePasswordChange?',
    name: 'Settings.ChangePassword',
    component: TheChangePassword,
    props: true,
    meta: {
      title: ['password_change_password_capitalized', 'settings']
    },
    beforeEnter: (to, from, next) => {
      const sessionStore = useSessionStore();
      const localizationStore = useLocalizationStore();

      if (sessionStore.hasPasscode)
        localizationStore.routeTitleKeys = ['passcode_change_cap', 'settings'];

      next();
    }
  },
  {
    path: '/Education/Technology/ROSAKnee',
    name: 'Education.Technology.ROSAKnee',
    component: TheROSAKneeEducationLibrary,
    meta: {
      title: ['rosa_education_rosa_knee_system_ios', 'pwe_technology_library', 'gen_education']
    }
  },
  {
    path: '/Education/RosaKnee/Epub/:itemId',
    name: 'Education.RosaKnee.Epub',
    component: Education,
    props: true
  },
  {
    path: '/Education/RosaKnee/Video/:itemId',
    name: 'Education.RosaKnee.Video',
    component: LibraryVideoItem,
    props: true
  },
  {
    path: '/Education/RosaKnee/Html/:itemId/:title?/:heading?',
    name: 'Education.RosaKnee.Html',
    component: LibraryHtmlItem,
    props: (route) => ({ ...route.params, title: i18n.global.t('rosa_education_rosa_knee_system_ios') })
  },
  {
    path: '/Education/PreSurgery/:itemId',
    name: 'Education.PreSurgery',
    component: Education,
    props: (route: any) => ({ ...route.params, ...{ itemId: Number.parseInt(route.params.itemId, 10), redirectPage: 'PreSurgery' } })
  },
  {
    path: '/Education/PostSurgery/:itemId',
    name: 'Education.PostSurgery',
    component: Education,
    props: (route: any) => ({ ...route.params, ...{ itemId: Number.parseInt(route.params.itemId, 10), redirectPage: 'PostSurgery' } })
  },
  {
    path: '/Education/:itemId/:tickSignature',
    name: 'Education.Education',
    component: Education,
    props: (route: any) => ({ ...route.params, ...{ itemId: Number.parseInt(route.params.itemId, 10) } }),
    meta: {
      title: ['gen_education']
    }
  },
  {
    path: '/Routine/:itemId/:tickSignature',
    name: 'Routine.Routine',
    component: Routine,
    props: true,
    meta: {
      title: ['gen_routine']
    }
  },
  {
    path: '/Routine/Exercise/:itemId/:exerciseId/:tickSignature',
    name: 'Routine.Exercise',
    component: Exercise,
    props: true,
    meta: {
      title: ['gen_exercise']
    }
  },
  {
    path: '/Routine/Rate/:itemId/:tickSignature',
    name: 'Routine.Rate',
    component: RateRoutine,
    props: true,
    meta: {
      title: ['routine_rate']
    }
  },
  {
    path: '/Routine/Completed/:completed/:rating',
    name: 'Routine.Completed',
    component: CompletedRoutine,
    props: true,
    meta: {
      title: ['watch_todo_completed_ios']
    }
  },
  {
    path: '/DailyWalkingGoal',
    name: 'DailyWalkingGoal',
    component: TheDailyWalkingGoal,
    meta: {
      title: ['gait_walking_progress']
    }
  },
  // This is just a placeholder for the Pain Check In Survey to represent the action of the Pain Rating button.
  {
    path: '/PainRating',
    name: 'PainRating',
    component: ThePainRating,
    meta: {
      policy: AuthPolicyEnum.FullTrustPatient,
      title: ['pain_rating_title']
    }
  },
  {
    path: '/AboutYourCommunicationPreferences',
    name: 'AboutYourCommunicationPreferences',
    component: AboutYourCommunicationPreferences,
    props: true,
    meta: {
      policy: AuthPolicyEnum.FullTrustPatient,
      title: ['communication_preferences_title']
    }
  },
  {
    path: '/CommunicationPreferences/:interrupt',
    name: 'CommunicationPreferences',
    component: CommunicationPreferences,
    props: (route: any) => ({ ...route.params, ...{ interrupt: route.params.interrupt == 'true' } }),
    meta: {
      policy: AuthPolicyEnum.FullTrustPatient,
      title: ['communication_preferences_title']
    }
  },
  {
    path: '/Survey/:surveyId/:tickSignature?/:pageNumber?',
    name: 'Survey',
    props: (route: any) => ({ ...route.params, ...{ surveyId: Number.parseInt(route.params.surveyId, 10) } }),
    component: TheSurveyContainer,
    meta: {
      policy: AuthPolicyEnum.FullTrustOrMagicLinkTrustPatient,
      title: ['gen_survey']
    }
  },
  {
    path: '/Engagement/:surveyId/:redirectRoute?',
    name: 'Engagement',
    props: (route: any) => ({ ...route.params, ...{ surveyId: Number.parseInt(route.params.surveyId, 10) }, redirectRoute: route.params.redirectRoute }),
    component: TheSurveyContainer,
    meta: {
      policy: AuthPolicyEnum.FullTrustPatient,
      title: ['gen_survey']
    }
  },
  {
    path: '/MAOnboarding/:surveyId/:redirectRoute?',
    name: 'MAOnboarding',
    props: (route: any) => ({ ...route.params, ...{ surveyId: Number.parseInt(route.params.surveyId, 10) }, redirectRoute: route.params.redirectRoute }),
    component: TheWizardContainer,
    meta: {
      policy: AuthPolicyEnum.FullTrustPatient,
      title: ['gen_survey']
    }
  },
  {
    path: '/Images/Medstrat',
    name: 'Images.MedStrat',
    component: medStratPage,
    meta: {
      policy: AuthPolicyEnum.FullTrustPatient,
      title: ['acc_image_viewer']
    }
  },
  {
    path: '/Procedure/set-surgery-date',
    name: 'Procedure.SetSurgeryDate',
    component: SetSurgeryDate,
    props: (route: any) => ({...route.params, selectedEocId: route.query.selectedEocId  }),
  },
  {
    path: '/Waitlist',
    name: 'Waitlist',
    component: TheWaitlistInterrupt,
    meta: {
      policy: AuthPolicyEnum.FullTrustPatient,
      title: ['waiting_list_interrupt_header']
    }
  },
  // Recovery Updates (EOC AI Summaries)
  {
    path: '/RecoveryUpdates/:eocId(\\d+)',
    name: 'RecoveryUpdates.List',
    component: RcvyUpdtsList,
    meta: {
      title: ['all_updates_title']
    },
    props: ({params}) => ({ eocId: Number(params.eocId) })
  },
  {
    path: '/RecoveryUpdates/:eocId(\\d+)/:sumId(\\d+)',
    name: 'RecoveryUpdates.Detail',
    component: RcvyUpdtsDetail,
    meta: {
      title: ['Recovery update']
    },
    props: ({params}) => ({
      eocId: Number(params.eocId),
      sumId: Number(params.sumId)
    })
  }
];

export default routes;
